// Google Sheet GViz JSON fetcher — header-based column mapping, no fixed indices
const GVIZ_URL =
  "https://docs.google.com/spreadsheets/d/14lu9MY-QHeNWmmhmvrPCTUD5Fv2j4I0vs8f-0wKk3cU/gviz/tq?tqx=out:json&gid=0";

export interface Product {
  id: string;
  status: string;
  category: string;
  subcategory: string;
  brand: string;
  title: string;
  description: string;
  price: string;
  main_image: string;
  images: string;
  affiliate_link: string;
  badge: string;
  created_at: string;
}

interface GvizCell {
  v: string | number | null;
  f?: string;
}

interface GvizRow {
  c: (GvizCell | null)[];
}

interface GvizCol {
  label: string;
  type: string;
}

interface GvizResponse {
  table: {
    cols: GvizCol[];
    rows: GvizRow[];
  };
}

function normalizeHeader(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

function getCellValue(cell: GvizCell | null): string {
  if (!cell) return "";
  if (cell.v === null || cell.v === undefined) return "";
  if (typeof cell.v === "number") return String(cell.v);
  if (typeof cell.v === "string") return cell.v.trim();
  if (typeof cell.v === "object" && cell.f) return String(cell.f).trim();
  return String(cell.v).trim();
}

function parseGvizResponse(text: string): Product[] {
  const match = text.match(
    /google\.visualization\.Query\.setResponse\((.*)\);?\s*$/s
  );
  if (!match || !match[1]) {
    throw new Error("Format GViz invalide — impossible d'extraire le JSON");
  }

  const data: GvizResponse = JSON.parse(match[1]);
  if (!data.table || !Array.isArray(data.table.cols) || !Array.isArray(data.table.rows)) {
    throw new Error("Structure GViz inattendue");
  }

  // GViz peut renvoyer les en-têtes comme première ligne, avec cols[].label vides.
  let headers = data.table.cols.map((col) => normalizeHeader(col.label || ""));
  let rows = data.table.rows;
  if (headers.every((h) => !h) && rows.length > 0) {
    headers = (rows[0].c || []).map((cell) => normalizeHeader(getCellValue(cell)));
    rows = rows.slice(1);
  }
  const requiredFields = [
    "id", "status", "category", "subcategory", "brand",
    "title", "description", "price", "main_image", "images",
    "affiliate_link", "badge", "created_at",
  ];

  const headerMap: Record<string, number> = {};
  headers.forEach((h, i) => {
    if (h) headerMap[h] = i;
  });

  // Log missing headers in dev to help debugging
  const missing = requiredFields.filter((f) => headerMap[f] === undefined);
  if (missing.length > 0 && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn("[GoogleSheet] Colonnes manquantes:", missing);
  }

  const products: Product[] = [];

  for (const row of rows) {
    const cells = Array.isArray(row.c) ? row.c : [];
    const get = (field: string): string => {
      const idx = headerMap[field];
      if (idx === undefined) return "";
      return getCellValue(cells[idx] ?? null);
    };

    products.push({
      id: get("id"),
      status: get("status"),
      category: get("category"),
      subcategory: get("subcategory"),
      brand: get("brand"),
      title: get("title"),
      description: get("description"),
      price: get("price"),
      main_image: get("main_image"),
      images: get("images"),
      affiliate_link: get("affiliate_link"),
      badge: get("badge"),
      created_at: get("created_at"),
    });
  }

  return products;
}

export async function fetchProductsFromGoogleSheet(): Promise<Product[]> {
  // Add a cache-buster so the browser always fetches fresh data
  const url = `${GVIZ_URL}&_cb=${Date.now()}`;
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) throw new Error(`HTTP ${response.status} — erreur Google Sheet`);
  const text = await response.text();
  return parseGvizResponse(text);
}